import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { coinThrowToLine } from '../../core/iching-core';
import { IChingCastMode, IChingCoinFace, IChingLinePosition, IChingLineValue } from '../../core/iching.models';
import { injectIChingTranslate } from '../iching-consult-translate';

interface LedgerRow {
  readonly position: IChingLinePosition;
  readonly value: IChingLineValue | undefined;
  readonly yang: boolean;
  readonly moving: boolean;
  /** Muntzijden van deze worp, alleen als ze bij de lijnwaarde horen. */
  readonly faces: readonly IChingCoinFace[] | undefined;
  readonly label: string;
}

/** Van boven naar beneden: lijn 6 bovenaan, lijn 1 onderaan, zoals in de figuur. */
const POSITIONS_TOP_DOWN: readonly IChingLinePosition[] = [6, 5, 4, 3, 2, 1];

const LABEL_KEYS: Readonly<Record<IChingLineValue, string>> = {
  6: 'humanDesign.iching.cast.lineMovingYin',
  7: 'humanDesign.iching.cast.lineYang',
  8: 'humanDesign.iching.cast.lineYin',
  9: 'humanDesign.iching.cast.lineMovingYang',
};

/**
 * Worp-overzicht van fase 2: het hexagram groeit rij per rij (lijn 6 bovenaan),
 * met per lijn de getekende lijn, de drie muntzijden (munten-modus), de som en
 * yang/yin/bewegend. Nog niet geworpen lijnen staan gedimd als "Worp k"; de
 * laatste muntworp licht kort op. In de directe modus enkel lijnen en labels.
 */
@Component({
  selector: 'hd-iching-cast-ledger',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; }
    .ledger { display: flex; flex-direction: column; gap: 6px; margin: 0; padding: 0; list-style: none; }
    .row {
      display: grid; align-items: center; column-gap: 10px; box-sizing: border-box; min-height: 24px; padding: 0 8px;
      grid-template-columns: 14px 64px 22px minmax(0, auto);
      border-radius: var(--radius-sm, 4px); font-size: 13px;
    }
    .ledger--coins .row { grid-template-columns: 14px 64px 70px 22px minmax(0, auto); }
    .ledger--direct .row { grid-template-columns: 14px 64px minmax(0, auto); }
    .pos { font-weight: 600; text-align: right; opacity: 0.7; }
    .line { display: block; width: 64px; height: 8px; overflow: visible; }
    .bar { fill: var(--hd-iching-line, var(--text-primary, currentColor)); }
    .placeholder { fill: none; stroke: var(--hd-iching-muted-border, rgba(0, 0, 0, 0.3)); stroke-dasharray: 4 3; }
    .mark-yang { fill: var(--hd-iching-bg, var(--surface, #f5f2ea)); stroke: var(--hd-iching-mark, var(--primary)); stroke-width: 2; }
    .mark-yin { stroke: var(--hd-iching-mark, var(--primary)); stroke-width: 2; }
    .coins { display: inline-flex; gap: 4px; }
    .coin {
      display: inline-flex; align-items: center; justify-content: center;
      width: 20px; height: 20px; border-radius: 50%; font-size: 10.5px; font-weight: 600;
      border: 1px solid var(--hd-iching-coin-border, rgba(0, 0, 0, 0.25));
      background: var(--hd-iching-coin-bg, rgba(212, 168, 105, 0.12)); color: var(--text-primary, inherit);
    }
    .coin.is-head { background: var(--hd-iching-coin-head-bg, rgba(212, 168, 105, 0.35)); }
    .sum { font-weight: 700; text-align: center; }
    .label { white-space: nowrap; }
    .row.is-moving .label { font-weight: 600; }
    .row.is-pending { opacity: 0.45; }
    .row.is-pending .label { font-style: italic; }
    .row.is-latest { animation: ledger-latest 1.6s ease-out; }
    @keyframes ledger-latest {
      from { background: var(--hd-iching-word-marked, rgba(212, 168, 105, 0.35)); }
      to { background: transparent; }
    }
    @media (prefers-reduced-motion: reduce) {
      .row.is-latest { animation: none; }
    }
  `,
  template: `
    <ol class="ledger" [class.ledger--coins]="showCoins()" [class.ledger--direct]="mode() === 'direct'">
      @for (row of rows(); track row.position) {
        <li
          class="row"
          [attr.data-line]="row.position"
          [class.is-pending]="row.value === undefined"
          [class.is-moving]="row.moving"
          [class.is-latest]="row.position === latest()"
        >
          <span class="pos">{{ row.position }}</span>
          <svg class="line" viewBox="0 0 100 12" preserveAspectRatio="none" aria-hidden="true">
            @if (row.value === undefined) {
              <rect class="placeholder" x="0.5" y="2.5" width="99" height="7" rx="1" />
            } @else if (row.yang) {
              <rect class="bar" x="0" y="2" width="100" height="8" rx="1" />
            } @else {
              <rect class="bar" x="0" y="2" width="42" height="8" rx="1" />
              <rect class="bar" x="58" y="2" width="42" height="8" rx="1" />
            }
            @if (row.moving && row.yang) {
              <circle class="mark-yang" cx="50" cy="6" r="5" />
            } @else if (row.moving) {
              <path class="mark-yin" d="M45 1 l10 10 M55 1 l-10 10" />
            }
          </svg>
          @if (showCoins()) {
            <span class="coins">
              @for (face of row.faces ?? []; track $index) {
                <span class="coin" [class.is-head]="face === 'head'" [attr.data-face]="face" [attr.title]="faceTitle(face)">
                  {{ letter(face) }}
                </span>
              }
            </span>
          }
          @if (mode() !== 'direct') {
            <span class="sum" data-test-id="iching-ledger-sum">{{ row.value ?? '' }}</span>
          }
          <span class="label" data-test-id="iching-ledger-label">{{ row.label }}</span>
        </li>
      }
    </ol>
  `,
})
export class HdIChingCastLedgerComponent {
  /** Lijnen onder→boven (index 0 = lijn 1); een lege plek = nog niet geworpen of ingevuld. */
  readonly lines = input.required<readonly (IChingLineValue | undefined)[]>();
  /** Per worp drie muntzijden; korter dan `lines` of leeg bij handmatig, duizendblad of direct. */
  readonly coinFaces = input<ReadonlyArray<readonly IChingCoinFace[]>>([]);
  readonly mode = input.required<IChingCastMode>();

  protected readonly t = injectIChingTranslate();

  protected readonly showCoins = computed(() => this.mode() === 'coins' && this.coinFaces().length > 0);

  /** In munten-modus licht de bovenste (laatst geworpen) lijn kort op. */
  protected readonly latest = computed<IChingLinePosition | undefined>(() => {
    const count = this.lines().length;
    return this.mode() === 'coins' && count > 0 ? (Math.min(count, 6) as IChingLinePosition) : undefined;
  });

  protected readonly rows = computed<LedgerRow[]>(() => {
    const lines = this.lines();
    const coinFaces = this.coinFaces();
    return POSITIONS_TOP_DOWN.map(position => {
      const value = lines[position - 1];
      if (value === undefined) {
        return {
          position,
          value,
          yang: false,
          moving: false,
          faces: undefined,
          label: this.t('humanDesign.iching.cast.pendingLine', { line: position }),
        };
      }
      const faces = coinFaces[position - 1];
      return {
        position,
        value,
        yang: value === 7 || value === 9,
        moving: value === 6 || value === 9,
        faces: faces?.length === 3 && coinThrowToLine(faces as [IChingCoinFace, IChingCoinFace, IChingCoinFace]) === value ? faces : undefined,
        label: this.t(LABEL_KEYS[value]),
      };
    });
  });

  protected letter(face: IChingCoinFace): string {
    return this.faceTitle(face).charAt(0).toUpperCase();
  }

  protected faceTitle(face: IChingCoinFace): string {
    return this.t(`humanDesign.iching.cast.${face}`);
  }
}
